<script>
import { CONVERSATION_PRIORITY } from '../../../../shared/constants/messages';

// Cinco cores cheias, uma por nivel, na escalada que o usuario ja conhece de
// outras ferramentas: frio -> quente. Os valores vieram da referencia da #1243
// (`PRIO_COLORS`/`PRIO_INK` do canvas "Sidebar Detalhes"), e a tinta do texto
// vem de la junto -- nao e escolha livre:
//
//   #64748b nenhuma   branco
//   #2563eb baixa     branco
//   #facc15 media     #1a1d23   <- amarelo pede tinta escura
//   #f97316 alta      #1a1d23   <- laranja tambem
//   #dc2626 urgente   branco
//
// Antes eram tres degraus de rampa tingida (`-75` de fundo, `-800` no icone,
// `-900` no texto). O fundo a 75 era claro demais para o chip existir de longe,
// que era justamente o trabalho dele.
//
// `/80` nas tres superficies que usam esta tabela -- o chip da lista, o do
// cabecalho da conversa e o polegar do trilho de prioridade do painel. A 100%
// as cinco tintas cheias batiam de frente com o branco da tela.
//
// Houve um passo intermediario com o chip em `/90` e so o polegar em `/80`,
// pela diferenca de area entre eles; a decisao foi uniformizar. Isto nao e de
// graca -- ver a conta de contraste no fim deste bloco.
//
// Cor literal, e nao token: estas cinco nao acompanham o tema. Fundo cheio com
// tinta fixa le nos dois -- um chip azul com texto branco e azul com texto
// branco no escuro tambem --, enquanto as rampas antigas invertiam sozinhas e
// exigiam a conta de contraste em dobro. Por isso nao ha variante `dark:` aqui.
//
// Contraste medido, pior caso entre os fundos onde as pecas vivem (branco,
// trilho cinza e tema escuro):
//
//   alfa  nenhuma  baixa  media  alta  urgente
//   100     4,76   5,17   11,02  6,02   4,83
//   /80     3,24   3,63    7,38  4,23   3,78   <- em uso
//
// Quatro dos cinco ficam entre 3,2 e 4,3: passam o piso de 3:1 de elemento
// grafico, nao o de 4,5:1 de texto normal. E uma troca consciente de
// legibilidade por sobriedade, pedida explicitamente -- nao um descuido. Quem
// quiser o caminho de volta: cada degrau de 5% devolve cerca de 0,2 de razao.
//
// A contagem de barras do glifo (1/2/3) acompanha a cor: quem nao distingue as
// hues ainda le o nivel pela forma.
//
// Exportado desde a #1243: o trilho de prioridade do painel de detalhes pinta
// as mesmas cinco opcoes, e duas copias da tabela divergem no primeiro ajuste
// de contraste. Mora aqui, e nao num `.js` de constantes, porque o Tailwind so
// varre `.vue` e `.erb` (`content` do tailwind.config.js) -- num `.js` estas
// classes deixariam de ser geradas e o chip perderia a cor.
//
// "Nenhuma" nao e um nivel e nao tem chip: ela existe so no trilho do painel,
// e por isso mora fora da tabela.
export const PRIORITY_NONE_CONFIG = {
  surface: 'bg-[#64748b]/80',
  ink: 'text-white',
};

export const PRIORITY_CONFIG = {
  [CONVERSATION_PRIORITY.LOW]: {
    label: 'CONVERSATION.PRIORITY.OPTIONS.LOW',
    surface: 'bg-[#2563eb]/80',
    ink: 'text-white',
  },
  [CONVERSATION_PRIORITY.MEDIUM]: {
    label: 'CONVERSATION.PRIORITY.OPTIONS.MEDIUM',
    surface: 'bg-[#facc15]/80',
    ink: 'text-[#1a1d23]',
  },
  [CONVERSATION_PRIORITY.HIGH]: {
    label: 'CONVERSATION.PRIORITY.OPTIONS.HIGH',
    surface: 'bg-[#f97316]/80',
    ink: 'text-[#1a1d23]',
  },
  [CONVERSATION_PRIORITY.URGENT]: {
    label: 'CONVERSATION.PRIORITY.OPTIONS.URGENT',
    surface: 'bg-[#dc2626]/80',
    ink: 'text-white',
  },
};

export default {
  name: 'PriorityMark',
  props: {
    priority: {
      type: String,
      default: '',
      validator: value =>
        [...Object.values(CONVERSATION_PRIORITY), ''].includes(value),
    },
    // Com rotulo dentro da conversa, onde ha largura e o nivel precisa ser lido
    // sem passar o mouse. Sem rotulo na lista, onde o chip divide a linha com o
    // nome do contato e o StatusBadge.
    showLabel: {
      type: Boolean,
      default: false,
    },
    size: {
      type: String,
      default: 'small',
      validator: value => ['small', 'medium'].includes(value),
    },
  },
  computed: {
    config() {
      return PRIORITY_CONFIG[this.priority] || null;
    },
    labelText() {
      return this.config ? this.$t(this.config.label) : '';
    },
    // O pill com rotulo ja diz o nivel por escrito; repetir no tooltip so
    // atrapalha quem usa leitor de tela (o texto sairia duas vezes).
    tooltip() {
      if (this.showLabel) return null;
      return {
        content: this.labelText,
        delay: { show: 1500, hide: 0 },
        hideOnClick: true,
      };
    },
    markClasses() {
      if (!this.config) return [];
      return [
        'priority-mark',
        `priority-mark--${this.size}`,
        this.showLabel ? 'priority-mark--labelled' : 'priority-mark--icon-only',
        this.config.surface,
        this.config.ink,
      ];
    },
    iconSize() {
      return this.size === 'medium' ? '16' : '14';
    },
  },
};
</script>

<template>
  <span
    v-if="config"
    v-tooltip="tooltip"
    :class="markClasses"
    :role="showLabel ? null : 'img'"
    :aria-label="showLabel ? null : labelText"
  >
    <fluent-icon
      class="shrink-0"
      :class="config.ink"
      :icon="`priority-${priority.toLowerCase()}`"
      :size="iconSize"
      view-box="0 0 14 14"
    />
    <span v-if="showLabel" class="priority-mark__label">{{ labelText }}</span>
  </span>
</template>

<style lang="scss" scoped>
// Pill, no mesmo desenho do StatusBadge, que fica ao lado dele na linha da
// lista -- dois chips com raio e altura diferentes na mesma linha leem como
// dois componentes sem relacao.
//
// O que estava errado antes: a caixa media 14x14 e o icone tambem 14, entao o
// fundo tingido nao tinha um pixel sequer para aparecer. Na pratica o chip era
// so o glifo, e o glifo era cinza em Baixa, Media e Alta.
.priority-mark {
  @apply inline-flex items-center justify-center rounded-full shrink-0
    font-medium whitespace-nowrap;

  &--small {
    @apply h-5 text-xxs;
  }

  &--medium {
    @apply h-6 text-xs;
  }

  // Quadrado: sem rotulo o pill vira um circulo, e o glifo fica centrado nele.
  &--icon-only {
    &.priority-mark--small {
      @apply w-5;
    }

    &.priority-mark--medium {
      @apply w-6;
    }
  }

  // O `shrink-0` da raiz serve a linha da lista, onde o pill nunca cede. No
  // cabecalho da conversa e o contrario, e por um motivo que so aparece
  // medindo: com o painel de contato aberto, a faixa do titulo cai de 526px
  // (1920) para 46px (1440) e para ZERO abaixo disso. O aperto e anterior a
  // esta mudanca -- a 1440 o nome ja saia com 44px --, mas o pill nao pode
  // piorar o que sobra. Dai o `shrink` e o `overflow-hidden` aqui:
  //
  //   - Sem eles o pill ficava rigido em 82px, estourava a linha de 46 e o
  //     `overflow-hidden` do bloco de titulo o cortava no meio da palavra,
  //     comendo o nome do contato inteiro -- que ali importa mais.
  //   - `shrink` sozinho tambem nao bastava: `min-width: auto` resolve para o
  //     min-content do item, e o min-content deste pill inclui a palavra
  //     inteira porque a raiz e `whitespace-nowrap`. `min-w-0` no rotulo nao
  //     ajuda: e piso, nao teto, e nao reduz a contribuicao de min-content do
  //     pai. Quem zera a largura minima automatica e o `overflow-hidden`; o
  //     piso volta explicito logo abaixo, na largura do chip de icone.
  //
  // Quem garante que o NOME cede primeiro nao esta aqui, e sim no
  // `shrink-[100]` do botao de titulo no ConversationHeader: com fator 100
  // contra 1 o nome absorve quase todo o deficit, congela em zero, e so entao
  // o flex redistribui o resto para ca. Com fator 1 nos dois, o pill saia
  // "Urge..." ja a 1920px. E um fator FRACIONARIO aqui nao substituiria aquele
  // 100: pela regra 9.7-4b do flexbox, quando a soma dos fatores dos itens nao
  // congelados e menor que 1 o espaco livre e multiplicado por essa soma -- o
  // pill deixaria de absorver o deficit final e a linha voltaria a estourar.
  &--labelled {
    // `justify-start`, e nao o `justify-center` da raiz: centralizar e
    // alinhamento "inseguro" -- quando o conteudo passa da caixa ele transborda
    // pelas DUAS pontas, e a ponta inicial e a que o `overflow-hidden` corta e
    // ninguem alcanca. No piso de 32px sobram 16px uteis para 66px de conteudo
    // rigido (o icone e `shrink-0` e o rotulo herda `whitespace-nowrap`, entao
    // nenhum dos dois encolhe): medido, o glifo aparecia com 6,9 dos seus 16px
    // e o que restava na tela era o MIOLO da palavra. Alinhado ao inicio o piso
    // mostra o glifo inteiro, que e a degradacao que este bloco promete.
    @apply justify-start gap-1 shrink overflow-hidden;

    // O piso e o chip de icone: padding + glifo. Valor cravado e nao
    // `min-w-5`/`min-w-6` porque a escala de `padding` do projeto e 75% da do
    // Tailwind e `spacing` nao foi tocada -- os dois utilitarios nao batem
    // mais, e um piso menor que o conteudo cortaria o proprio glifo. Pela
    // escala do tailwind.config.js os degraus colapsam na grade de 4px:
    // `px-1.5` da 4px de cada lado (14 + 8 = 22) e `px-2` da 8 (16 + 16 = 32,
    // conferido no navegador).
    &.priority-mark--small {
      @apply px-1.5 min-w-[22px];
    }

    &.priority-mark--medium {
      @apply px-2 min-w-[32px];
    }
  }

  // Sem `truncate` de proposito. O `text-overflow: ellipsis` reage a um
  // deficit SUB-PIXEL: o rotulo mede 46,33px de max-content, o flex lhe
  // entregava 46,06px (a fatia de 0,27px que o pill cede do deficit da linha) e
  // isso bastava para "Urgente" virar "Urgen..." numa tela de 1920px, com
  // espaco de sobra. Sem ellipsis quem corta e o `overflow-hidden` do pill, e
  // um corte de 0,27px cai no antialiasing da ultima letra -- invisivel. O
  // rotulo tambem nao precisa de `min-w-0`: mantendo o min-content dele (a
  // palavra inteira, por causa do `whitespace-nowrap` da raiz) o pill encolhe
  // ate o piso e o texto e recortado, em vez de espremido.
  &__label {
    // O -900 do texto vem da raiz; o icone traz o -800 na propria classe.
    @apply leading-none;
  }
}
</style>
